
import { Box, ButtonBase, useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";

import AddIcon from '@mui/icons-material/Add';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined';
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import { useExportInvoiceProductsMutation, useGetInvoiceProductsQuery } from "../../../../../newapi/financial/invoiceProductSlice";
import { showToastMessage } from "../../../../../utils/toasts";
import { general_states, getInvoiceProductType, getInvoiceProductTypeLabel } from "../../../../../utils/staticEnums";

import MAddInvoiceProduct from "../../../../../components/global/Modals/MAddInvoiceProduct";
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShDataGridSelectedActions from "../../../../../components/theme/datagrid2/ShDataGridSelectedActions";
import Text from "../../../../../components/theme/text/Text";

import { WarehouseInvoiceProductCard } from "../../../../../components/global/cards/financial/InvoiceProductCard";
import MEditProductPrices from "../../../../../components/global/Modals/MEditProductPrices";
import MImportData from "../../../../../components/global/Modals/MImportData";
import { ImportStandAloneFileLogo } from "../../../../../utils/importColumnDefinitions";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import { useSelector } from "react-redux";
import { INVOICE_PRODUCT_FILTER_KEYS } from "../../../../../components/global/DataGrid/Filter";

const SpInvoiceProductDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpInvoiceProductDetails/SpInvoiceProductDetails"));

const GridActions = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    return (
        <ButtonGroup version={2}>
            <Shbutton
                onClick={props.handleExport}
                className={"group-btn"}
                color={"primaryGrey"}
                variant="contained"
                endIcon={<DownloadOutlinedIcon/>}
            >
                {t('export')}
            </Shbutton>
            {props.hasModifyPrivilege && props.hasImportPrivilege && (
            <Shbutton
                onClick={props.handleImportOpen}
                className={"group-btn"}
                color={"primaryGrey"}
                variant="contained"
                endIcon={<UploadOutlinedIcon/>}
            >
                {t('import')}
            </Shbutton>
            )}
            {props.hasModifyPrivilege && (
            <Shbutton 
                className={"group-btn"} 
                color={theme.palette.mode === "dark" ? "blue" : "primary"} 
                onClick={() => props.handleOpen(true)}
                endIcon={<AddIcon/>}
                variant="contained"
            >
                {t('new_invoice_product')}
            </Shbutton>
            )}
        </ButtonGroup>
    );
}

let gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [INVOICE_PRODUCT_FILTER_KEYS.PRODUCT_TYPE, INVOICE_PRODUCT_FILTER_KEYS.PRODUCT_CATEGORY],
};

const Products = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [isExtended, setIsExtended] = useState(false);
    // const [selectedRowId, setSelectedRowId] = useState(-1);
    // const [rowSelected, setRowSelected] = useState(false);

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.PRICEINCREASE_MANAGE])
    );

    // The import runs through the shared single import, which demands the import privilege.
    const hasImportPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.IMPORT_MANAGE])
    );
    
    const [spLoading, setSpLoading] = useState();

    const ref = useRef();
    const [config, setConfig] = useState({"state": general_states.ACTIVE});

    const [open, setOpen] = useState(false);

    const createContainerSizeModal = {
        open: open,
        setOpen: setOpen,
    }

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions handleOpen={handleOpen} handleExport={handleExportInvoiceProducts} handleImportOpen={handleImportOpen} hasModifyPrivilege={hasModifyPrivilege} hasImportPrivilege={hasImportPrivilege}></GridActions>
        });

        return () => {
        }
    },[]);

    const handleOpen = () => {
        setAddModalOpen(true);
    }

    const [exportInvoiceProducts, {isLoading: isExporting}] = useExportInvoiceProductsMutation();
    const handleExportInvoiceProducts = () => {
        if (isExporting) return;

        exportInvoiceProducts().unwrap()
        .then(res => {
            // A long-running export is finished in the background and announced by notification
            showToastMessage("success", res.processing ? t("export_processing") : t("export_ready"));
        })
        .catch(e => {
            console.error(e);
            showToastMessage("error", t("export_failed"));
        });
    }

    const [selectedItems, setSelectedItems] = useState([]);
    const onSelected = (selectedItems) => {
        setSelectedItems(selectedItems);
    }
    
      // Function to select or deselect a card by its ID
      const handleCardSelection = (id) => {
        setSelectedItems((prevSelectedIds) => {
        if (prevSelectedIds.includes(id)) {
            // Deselect the card if it's already selected
            return prevSelectedIds.filter((selectedId) => selectedId !== id);
        } else {
            // Select the card if it's not selected
            return [...prevSelectedIds, id];
        }
        });
    };
    

    const [addModalOpen, setAddModalOpen] = useState(false);
    const addModal = {
        open: addModalOpen,
        setOpen: setAddModalOpen
    }

    const handleClose = () => {
        setAddModalOpen(false);
    };

    const [importModalOpen, setImportModalOpen] = useState(false);
    const handleImportOpen = () => {
        setImportModalOpen(true);
    };

    // The tarieven export read back: new category columns become categories, existing
    // categories only take the prices when the user asks for it.
    const importColumns = [
        {
            column: "Tarief",
            required: true
        },
        {
            column: t("invoice_category_prices_import_category_column"),
            required: true
        }
    ];

    const importSettings = [
        {
            name: "updateExistingPrices",
            label: t("invoice_category_prices_import_update_existing"),
            type: "boolean",
            defaultValue: false
        }
    ];

    const [invoiceProductId, setInvoiceProductId] = useState();
    const invoiceProductIdRef = useRef(invoiceProductId);
    
    const getInvoiceProduct = (params) => {
        if (!params) return;
    
        const newInvoiceProductId = parseInt(params.row.id);
        // Only update state if the pickingJobListId has actually changed
        if (invoiceProductIdRef.current !== newInvoiceProductId) {
            
            if(!spLoading) {
                setSpLoading(true);
            }
            setInvoiceProductId(newInvoiceProductId);
            invoiceProductIdRef.current = newInvoiceProductId; // Update the ref to the new value
        }

            setIsExtended(true);
    }

    const columns = [
        {
            field: "name",
            headerName: t("name"),
            flex:2,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
    
                return (
                    <Box 
                        sx={{
                            width:'100%',
                            cursor:"pointer",
                            lineHeight:"64px",
                        }} 
                        onClick={(event) => {
                            event.stopPropagation();
                            // getPickingList(params);
                            getInvoiceProduct(params);
                        }}
                    >
                        <Text clickable semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{params.row.name}</Text>
                    </Box>
                );
                // return params.row.name;
            }
        },
        {
            field: "invoiceline",
            headerName: t("invoiceline"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
    
                return params.row.invoiceline;
            }
        },
        {
            field: "type", 
            headerName: t("type"),
            flex:1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />;
                }

                // type and subType are two halves of one named type, shown as one label.
                const productType = getInvoiceProductType(params.row.type, params.row.subType);

                return (
                    <ShLabel fitted size={32} minWidth={150} variant={productType?.color ?? "grey"}>
                        {getInvoiceProductTypeLabel(t, params.row.type, params.row.subType)}
                    </ShLabel>
                );
            }
        },
        {field: "ledger.description", headerName: t("ledger"),flex:1,
        renderCell: (params) => {
            if(params.row.skeleton) {
                return <ShTextSkeleton/>
            }
            
            return params.row.ledger?.description ?? "";
        }},
        {field: "taxRate.description", headerName: t("tax_rate"),flex:1,
        renderCell: (params) => {
            if(params.row.skeleton) {
                return <ShTextSkeleton/>
            }
            
            return params.row.taxRate?.description ?? "";
        }},
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <WarehouseInvoiceProductCard
                        select={handleCardSelection} 
                        selected={selectedItems.includes(params.row.id)}
                        onClick={() => getInvoiceProduct(params)}
                        skeleton={params.row.skeleton}
                        {...params.row}
                        canSelect={params.row.priceIncreases?.length === 0}
                    />
                    // <ShTextSkeleton/>
                )
            }
        }
    ];

    return (
        <Box height={1} ref={ref} display={"flex"} flexDirection={"column"}>
            {/* <GridActions></GridActions> */}
            <ShDataGrid2 key={theme.palette.mode}
                content={useGetInvoiceProductsQuery}
                config={config}
                gridActions={<GridActions handleOpen={handleOpen} handleExport={handleExportInvoiceProducts} handleImportOpen={handleImportOpen} hasModifyPrivilege={hasModifyPrivilege} hasImportPrivilege={hasImportPrivilege}></GridActions>} 
                title={t('invoice_products')}
                gridOptions={gridOptions} 
                columns={columns}
                rows={data}
                // onRowClick={getInvoiceProduct}
                sortModel={{field: columns[0].field,type:"ASC"}}
                selectable
                keepSelectedAlive
                keepNonExistentRowsSelected
                setSelected={onSelected}
                selectedItems={selectedItems}
                selectedActions={
                    <ShDataGridSelectedActions showButtons selectedItems={selectedItems}>
                        <ButtonBase
                            onClick={() => createContainerSizeModal.setOpen(true)}
                            sx={{
                                background:tokens('light').txt["white"],
                                height:40,
                                borderRadius:7,
                                fontSize:"14px",
                                fontFamily:"Roboto, sans-serif",
                                px:3,
                                boxShadow:"none",
                                border:"none",
                                fontWeight:500,
                                color:tokens('light').txt["primary"]
                            }}
                        >
                            {t('change_prize')}
                        </ButtonBase>
                    </ShDataGridSelectedActions>
                }
                isRowSelectable={(row) => {
                    if(!hasModifyPrivilege || row.priceIncreases?.length > 0) {
                        return false;
                    }
                    return true;
                    // return row.priceIncreases?.length > 0
                }}
            >
            </ShDataGrid2>

            {spLoading && 
                <SpInvoiceProductDetails
                    invoiceProductId={invoiceProductId}
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    // data={pickingJobData}
                /> 
            }
            
            <MEditProductPrices
                selectedItems={selectedItems.length}
                items={selectedItems}
                open={createContainerSizeModal.open}
                handleClose={() => createContainerSizeModal.setOpen(false)}
            />
            <MImportData
                open={importModalOpen}
                handleClose={() => setImportModalOpen(false)}
                logo={<ImportStandAloneFileLogo Icon={CallMadeOutlinedIcon} />}
                title={t("import_invoice_category_prices")}
                fileType={"tariefcategorieen"}
                columns={importColumns}
                notice={t("invoice_category_prices_import_explainer")}
                importSettings={importSettings}
            />
            <MAddInvoiceProduct onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose} />
        </Box>
    );
}

export default Products;